import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Briefcase, Plus, Search, Loader2, X, Edit2, Trash2,
  User, Building2, Calendar, AlertCircle, Scale, MapPin
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import { confirmToast } from '@/utils/confirmToast';
import Sidebar from '@/components/Sidebar';

const STATUS_OPTIONS = ['Active', 'Pending', 'Closed', 'On Hold'];
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];
const CASE_TYPE_OPTIONS = ['Dissolution', 'DVRO', 'Child Custody', 'Spousal Support', 'Property'];

const STATUS_COLORS = {
  'Active': 'bg-green-100 text-green-700',
  'Pending': 'bg-yellow-100 text-yellow-700',
  'Closed': 'bg-slate-100 text-slate-600',
  'On Hold': 'bg-orange-100 text-orange-700'
};

const PRIORITY_COLORS = {
  'High': 'bg-red-100 text-red-700',
  'Medium': 'bg-blue-100 text-blue-700',
  'Low': 'bg-slate-100 text-slate-600'
};

export default function CasesPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [opposingCounsels, setOpposingCounsels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddCase, setShowAddCase] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const emptyCase = {
    court_case_number: '',
    case_title: '',
    case_types: [],
    status: 'Active',
    description: '',
    client_id: '',
    attorney_id: '',
    opposing_counsel_id: '',
    opposing_party_name: '',
    filing_date: '',
    next_hearing_date: '',
    trial_date: '',
    court_name: '',
    judge_name: '',
    priority: 'Medium'
  };
  
  const [caseForm, setCaseForm] = useState(emptyCase);

  useEffect(() => {
    const initData = async () => {
      await fetchUser();
      await Promise.all([
        fetchCases(),
        fetchClients(),
        fetchAttorneys(),
        fetchOpposingCounsels()
      ]);
    };
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        if (userData.app_mode !== 'crm') {
          toast.error('CRM features require CRM subscription');
          navigate('/dashboard');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchCases = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/cases`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCases(data);
      }
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchAttorneys = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/firm`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAttorneys(data.attorneys || []);
      }
    } catch (error) {
      console.error('Error fetching attorneys:', error);
    }
  };

  const fetchOpposingCounsels = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/opposing-counsel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOpposingCounsels(data);
      }
    } catch (error) {
      console.error('Error fetching opposing counsels:', error);
    }
  };

  const handleSaveCase = async () => {
    if (!caseForm.court_case_number.trim()) {
      toast.error('Court case number is required');
      return;
    }
    if (!caseForm.case_title.trim()) {
      toast.error('Case title is required');
      return;
    }
    if (caseForm.case_types.length === 0) {
      toast.error('Select at least one case type');
      return;
    }
    if (!caseForm.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (!caseForm.attorney_id) {
      toast.error('Please select an attorney');
      return;
    }
    if (!caseForm.opposing_party_name.trim()) {
      toast.error('Opposing party name is required');
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const url = editingCase 
        ? `${API}/crm/cases/${editingCase.case_id}`
        : `${API}/crm/cases`;
      
      const response = await fetch(url, {
        method: editingCase ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(caseForm)
      });
      
      if (response.ok) {
        toast.success(editingCase ? 'Case updated' : 'Case created');
        setShowAddCase(false);
        setEditingCase(null);
        setCaseForm(emptyCase);
        fetchCases();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save case');
      }
    } catch (error) {
      toast.error('Failed to save case');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCase = async (caseId) => {
    const caseItem = cases.find(c => c.case_id === caseId);
    const caseName = caseItem?.case_title || 'this case';
    
    confirmToast(
      `Are you sure you want to delete "${caseName}"? This will also delete all associated notes. This action cannot be undone.`,
      async () => {
        const token = localStorage.getItem('token');
        
        try {
          const response = await fetch(`${API}/crm/cases/${caseId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            toast.success('Case deleted successfully');
            fetchCases();
          } else {
            const error = await response.json();
            toast.error(error.detail || 'Failed to delete case');
          }
        } catch (error) {
          toast.error('Failed to delete case');
        }
      },
      { title: 'Delete Case' }
    );
  };

  const handleEditCase = (caseItem) => {
    setCaseForm({
      court_case_number: caseItem.court_case_number || '',
      case_title: caseItem.case_title || '',
      case_types: caseItem.case_types || [],
      status: caseItem.status || 'Active',
      description: caseItem.description || '',
      client_id: caseItem.client_id || '',
      attorney_id: caseItem.attorney_id || '',
      opposing_counsel_id: caseItem.opposing_counsel_id || '',
      opposing_party_name: caseItem.opposing_party_name || '',
      filing_date: caseItem.filing_date || '',
      next_hearing_date: caseItem.next_hearing_date || '',
      trial_date: caseItem.trial_date || '',
      court_name: caseItem.court_name || '',
      judge_name: caseItem.judge_name || '',
      priority: caseItem.priority || 'Medium'
    });
    setEditingCase(caseItem);
    setShowAddCase(true);
  };

  const toggleCaseType = (type) => {
    if (caseForm.case_types.includes(type)) {
      setCaseForm({
        ...caseForm,
        case_types: caseForm.case_types.filter(t => t !== type)
      });
    } else {
      setCaseForm({
        ...caseForm,
        case_types: [...caseForm.case_types, type]
      });
    }
  };

  const filteredCases = cases.filter(caseItem => {
    const search = searchQuery.toLowerCase();
    return caseItem.court_case_number?.toLowerCase().includes(search) ||
      caseItem.internal_case_number?.toLowerCase().includes(search) ||
      caseItem.case_title?.toLowerCase().includes(search) ||
      caseItem.client_name?.toLowerCase().includes(search) ||
      caseItem.opposing_party_name?.toLowerCase().includes(search);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8 lg:pt-8 pt-20">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Cases</h1>
            <p className="text-slate-500">{cases.length} case{cases.length !== 1 ? 's' : ''}</p>
          </div>
          <Button
            onClick={() => navigate('/cases/new')}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
            data-testid="add-case-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Case
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by case number, title, or client..."
            className="pl-12 h-12 rounded-xl bg-white"
            data-testid="search-cases"
          />
        </div>

        {/* Cases Table */}
        {filteredCases.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {searchQuery ? 'No cases found' : 'No cases yet'}
            </h3>
            <p className="text-slate-500 mb-6">
              {searchQuery 
                ? 'Try a different search term' 
                : 'Add your first case to get started'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate('/cases/new')}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Case
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="cases-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Case #</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Title</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Client</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Type(s)</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Status</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Next Hearing</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Attorney</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredCases.map((caseItem) => (
                    <tr
                      key={caseItem.case_id}
                      onClick={() => navigate(`/cases/${caseItem.case_id}`)}
                      className="hover:bg-violet-50 transition-colors cursor-pointer"
                      data-testid={`case-row-${caseItem.case_id}`}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold text-slate-900 text-sm">{caseItem.court_case_number}</p>
                          <p className="text-xs text-slate-500">{caseItem.internal_case_number}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-900">{caseItem.case_title}</p>
                        {caseItem.opposing_party_name && (
                          <p className="text-xs text-slate-500">vs. {caseItem.opposing_party_name}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-600">{caseItem.client_name || '—'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {caseItem.case_types?.slice(0, 2).map((type, idx) => (
                            <span key={idx} className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs font-medium">
                              {type}
                            </span>
                          ))}
                          {caseItem.case_types?.length > 2 && (
                            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">
                              +{caseItem.case_types.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[caseItem.status] || 'bg-slate-100 text-slate-600'}`}>
                          {caseItem.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {caseItem.next_hearing_date ? (
                          <p className="text-sm text-slate-600 flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {new Date(caseItem.next_hearing_date).toLocaleDateString()}
                          </p>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-600">{caseItem.attorney_name || '—'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/cases/edit/${caseItem.case_id}`);
                            }}
                            className="rounded-lg h-8 w-8 p-0 hover:bg-violet-50 hover:text-violet-700"
                            data-testid={`edit-case-btn-${caseItem.case_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCase(caseItem.case_id);
                            }}
                            className="rounded-lg h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-case-btn-${caseItem.case_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      {/* Add/Edit Case Modal */}
      {showAddCase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full my-8">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
              <h2 className="text-xl font-semibold">
                {editingCase ? 'Edit Case' : 'Add New Case'}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowAddCase(false); setEditingCase(null); }}
                className="rounded-lg"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
              {/* Case Identification */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Scale className="w-4 h-4" />
                  Case Identification
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">Court Case Number *</Label>
                      <Input
                        value={caseForm.court_case_number}
                        onChange={(e) => setCaseForm({ ...caseForm, court_case_number: e.target.value })}
                        placeholder="FL-2024-001234"
                        className="mt-1"
                        data-testid="court-case-number-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Case Title *</Label>
                      <Input
                        value={caseForm.case_title}
                        onChange={(e) => setCaseForm({ ...caseForm, case_title: e.target.value })}
                        placeholder="Smith vs. Smith"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm text-slate-600">Case Type(s) * (Select all that apply)</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {CASE_TYPE_OPTIONS.map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => toggleCaseType(type)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            caseForm.case_types.includes(type)
                              ? 'bg-violet-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`case-type-${type.toLowerCase().replace(' ', '-')}`}
                        >
                          {caseForm.case_types.includes(type) && '✓ '}
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">Status</Label>
                      <select
                        value={caseForm.status}
                        onChange={(e) => setCaseForm({ ...caseForm, status: e.target.value })}
                        className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                      >
                        {STATUS_OPTIONS.map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Priority</Label>
                      <select
                        value={caseForm.priority}
                        onChange={(e) => setCaseForm({ ...caseForm, priority: e.target.value })}
                        className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                      >
                        {PRIORITY_OPTIONS.map(priority => (
                          <option key={priority} value={priority}>{priority}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Parties */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Parties
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">Client *</Label>
                      <select
                        value={caseForm.client_id}
                        onChange={(e) => setCaseForm({ ...caseForm, client_id: e.target.value })}
                        className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                        data-testid="client-select"
                      >
                        <option value="">Select Client</option>
                        {clients.map(client => (
                          <option key={client.client_id} value={client.client_id}>
                            {client.full_name}
                          </option>
                        ))}
                      </select>
                      {clients.length === 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          No clients found. <button onClick={() => navigate('/clients')} className="text-violet-600 hover:underline">Add a client</button>
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Assigned Attorney *</Label>
                      <select
                        value={caseForm.attorney_id}
                        onChange={(e) => setCaseForm({ ...caseForm, attorney_id: e.target.value })}
                        className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                      >
                        <option value="">Select Attorney</option>
                        {attorneys.map(attorney => (
                          <option key={attorney.attorney_id} value={attorney.attorney_id}>
                            {attorney.full_name}
                          </option>
                        ))}
                      </select>
                      {attorneys.length === 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          No attorneys found. <button onClick={() => navigate('/firm')} className="text-violet-600 hover:underline">Add an attorney</button>
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm text-slate-600">Opposing Party Name *</Label>
                    <Input
                      value={caseForm.opposing_party_name}
                      onChange={(e) => setCaseForm({ ...caseForm, opposing_party_name: e.target.value })}
                      placeholder="Jane Doe"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm text-slate-600">Opposing Counsel (Optional - Leave blank for Pro Se)</Label>
                    <select
                      value={caseForm.opposing_counsel_id}
                      onChange={(e) => setCaseForm({ ...caseForm, opposing_counsel_id: e.target.value })}
                      className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                    >
                      <option value="">Pro Se (Self-Represented)</option>
                      {opposingCounsels.map(counsel => (
                        <option key={counsel.counsel_id} value={counsel.counsel_id}>
                          {counsel.first_name} {counsel.last_name} - {counsel.law_firm || 'Independent'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Important Dates */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Important Dates
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600">Filing Date</Label>
                    <Input
                      type="date"
                      value={caseForm.filing_date}
                      onChange={(e) => setCaseForm({ ...caseForm, filing_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Next Hearing</Label>
                    <Input
                      type="date"
                      value={caseForm.next_hearing_date}
                      onChange={(e) => setCaseForm({ ...caseForm, next_hearing_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Trial Date</Label>
                    <Input
                      type="date"
                      value={caseForm.trial_date}
                      onChange={(e) => setCaseForm({ ...caseForm, trial_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Court Information */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Court Information
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-slate-600">Court Name</Label>
                      <Input
                        value={caseForm.court_name}
                        onChange={(e) => setCaseForm({ ...caseForm, court_name: e.target.value })}
                        placeholder="Superior Court of California"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-slate-600">Judge Name</Label>
                      <Input
                        value={caseForm.judge_name}
                        onChange={(e) => setCaseForm({ ...caseForm, judge_name: e.target.value })}
                        placeholder="Hon. John Smith"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-sm text-slate-600">Case Description / Notes</Label>
                <Textarea
                  value={caseForm.description}
                  onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })}
                  placeholder="Enter case details, notes, or important information..."
                  className="mt-1 min-h-[100px]"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white rounded-b-2xl">
              <Button
                variant="outline"
                onClick={() => { setShowAddCase(false); setEditingCase(null); }}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCase}
                disabled={saving}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="save-case-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  editingCase ? 'Update Case' : 'Create Case'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
